import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { getErrorMessage } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "./useAuth";

export function useComments(ticketId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const commentsQuery = useQuery({
    queryKey: ["comments", ticketId],
    queryFn: async () => {
      if (!ticketId) return [];
      return await apiClient.getComments(ticketId);
    },
    enabled: !!ticketId,
  });

  const addCommentMutation = useMutation({
    mutationFn: async ({
      content,
      isInternal,
    }: {
      content: string;
      isInternal: boolean;
    }) => {
      if (!ticketId) throw new Error("No ticket ID");
      return await apiClient.createComment(ticketId, {
        content,
        isInternal,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["ticket_detail", ticketId] });
      toast.success("Comment added successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to add comment"));
    },
  });

  return {
    comments: commentsQuery.data ?? [],
    isLoading: commentsQuery.isLoading,
    error: commentsQuery.error,
    addComment: addCommentMutation,
  };
}

export function usePublicComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ticketId,
      content,
    }: {
      ticketId: string;
      content: string;
    }) => {
      // TODO: Implement public comment API endpoint
      return { ticketId, content };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ticket-by-token"] });
      toast.success("Reply sent successfully");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to send reply"));
    },
  });
}
